import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { FiPlus, FiChevronRight, FiInstagram, FiFacebook, FiYoutube, FiTwitter, FiLinkedin, FiTrendingUp, FiTrendingDown, FiCalendar, FiBarChart2, FiCheckCircle, FiXCircle, FiZap, FiUpload, FiTrash2, FiLoader, FiStar, FiArrowRight } from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import CampaignCreationModal from '../components/CampaignCreationModal';
import AILearningInsights from '../components/AILearningInsights';

function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showNewClientHint, setShowNewClientHint] = useState(false);
  
  // Brand Assets state
  const [brandAssets, setBrandAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedAssetType, setSelectedAssetType] = useState('logo');
  
  // Visual Intelligence / Brand Type state
  const [brandTypeInfo, setBrandTypeInfo] = useState(null);
  const [showBrandTypeSettings, setShowBrandTypeSettings] = useState(false);
  const [updatingBrandType, setUpdatingBrandType] = useState(false);

  useEffect(() => {
    loadClientData();
    
    // Check if this is a newly created client
    if (location.state?.newClient || location.state?.showCampaignHint) {
      setShowNewClientHint(true);
      // Clear the state to prevent re-showing
      window.history.replaceState({}, document.title);
    }
  }, [clientId, location.state]);

  const loadClientData = async () => {
    try {
      const [clientData, campaignsData, insightsData, assetsData] = await Promise.all([
        api.getClient(clientId),
        api.listCampaigns(clientId),
        api.getClientInsights(clientId).catch(() => null),
        api.getBrandAssets(clientId).catch(() => ({ brand_assets: [] }))
      ]);
      setClient(clientData);
      setCampaigns(campaignsData);
      setInsights(insightsData);
      setBrandAssets(assetsData.brand_assets || []);
      
      // Fetch brand type info
      fetchBrandTypeInfo();
    } catch (error) {
      console.error('Error loading client:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch Visual Intelligence brand type info
  const fetchBrandTypeInfo = async () => {
    try {
      const token = localStorage.getItem('firstcut_session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/brand-type/${clientId}`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setBrandTypeInfo(data);
      }
    } catch (error) {
      console.error('Error fetching brand type:', error);
    }
  };
  
  // Update brand type
  const updateBrandType = async (newType) => {
    setUpdatingBrandType(true);
    try {
      const token = localStorage.getItem('firstcut_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/brand-type/${clientId}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ content_focus_type: newType })
      });
      if (response.ok) {
        const data = await response.json();
        setBrandTypeInfo(data);
        setShowBrandTypeSettings(false);
        // Update local client state too
        setClient(prev => ({ ...prev, content_focus_type: newType }));
      }
    } catch (error) {
      console.error('Error updating brand type:', error);
    } finally {
      setUpdatingBrandType(false);
    }
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploadingAsset(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const result = await api.addBrandAsset(clientId, {
          image_url: reader.result,
          asset_type: selectedAssetType,
          name: file.name.split('.')[0]
        });
        
        if (result.asset) {
          setBrandAssets(prev => [...prev, result.asset]);
        }
      } catch (error) {
        console.error('Failed to upload asset:', error);
        alert('Failed to upload asset. Please try again.');
      } finally {
        setUploadingAsset(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteAsset = async (assetId) => {
    if (!confirm('Delete this brand asset?')) return;
    
    setDeletingAssetId(assetId);
    try {
      await api.deleteBrandAsset(clientId, assetId);
      setBrandAssets(prev => prev.filter(a => a.id !== assetId));
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    } finally {
      setDeletingAssetId(null);
    }
  };

  const assetTypeIcons = {
    logo: { icon: FiStar, label: 'Brand Logo', color: 'text-yellow-400' }
  };

  const socialIcons = {
    instagram: { icon: FiInstagram, color: 'text-pink-400', label: 'Instagram' },
    facebook: { icon: FiFacebook, color: 'text-blue-400', label: 'Facebook' },
    youtube: { icon: FiYoutube, color: 'text-red-400', label: 'YouTube' },
    tiktok: { icon: SiTiktok, color: 'text-foreground', label: 'TikTok' },
    twitter: { icon: FiTwitter, color: 'text-sky-400', label: 'Twitter/X' },
    linkedin: { icon: FiLinkedin, color: 'text-blue-400', label: 'LinkedIn' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading client...</p>
        </div>
      </div>
    );
  }

  // Show helpful onboarding state instead of just "Client not found"
  if (!client) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FiPlus className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Client Not Found</h2>
          <p className="text-muted-foreground mb-6">
            This client doesn&apos;t exist or may have been deleted. Let&apos;s get you started with a new one!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/clients')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
            >
              <FiPlus className="w-5 h-5" />
              Create Your First Client
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-secondary text-secondary-foreground rounded-xl font-medium hover:bg-secondary/80 transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasSocialHandles = client.social_handles && Object.values(client.social_handles).some(v => v);
  const hasSeasonalTrends = client.seasonal_trends && (client.seasonal_trends.peak_months?.length > 0 || client.seasonal_trends.low_months?.length > 0);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <button onClick={() => navigate('/clients')} className="hover:text-primary transition-colors">Clients</button>
          <span>/</span>
          <span className="text-foreground">{client.name}</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">{client.name}</h1>
            <p className="text-muted-foreground mt-1">{client.industry}</p>
          </div>
          <button
            onClick={() => setShowCampaignModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
            data-testid="new-campaign-btn"
          >
            <FiPlus size={18} />
            <span>New Campaign</span>
          </button>
        </div>
      </div>

      {/* New Client Welcome Hint - Guide to next step */}
      {showNewClientHint && campaigns.length === 0 && (
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-xl p-6 animate-fade-in-up">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">🎉</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Great! {client.name} is set up!
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Next step: Create your first campaign. A campaign defines what you want to achieve 
                and the creative direction for your content.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setShowNewClientHint(false);
                    setShowCampaignModal(true);
                  }}
                  className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all flex items-center gap-2"
                >
                  Create First Campaign
                  <FiArrowRight size={16} />
                </button>
                <button
                  onClick={() => setShowNewClientHint(false)}
                  className="px-5 py-2.5 text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  I&apos;ll do it later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border/50">
          <h3 className="font-semibold text-foreground mb-3">Brand Identity</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Tone:</span>
              <span className="ml-2 font-medium text-foreground">{client.brand_tone}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Personality:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {client.brand_personality.map((p, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">{p}</span>
                ))}
              </div>
            </div>
            {/* Content Focus Type - Visual Intelligence indicator */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Content Focus:</span>
                <button
                  onClick={() => setShowBrandTypeSettings(true)}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                  data-testid="edit-content-focus-btn"
                >
                  Edit
                </button>
              </div>
              <div className="mt-1 font-medium text-foreground">
                {client.detected_content_type || client.content_focus_type ? (
                  <>
                    {(client.detected_content_type || client.content_focus_type) === 'product' && '📦 Product-focused'}
                    {(client.detected_content_type || client.content_focus_type) === 'space' && '🏠 Space-focused'}
                    {(client.detected_content_type || client.content_focus_type) === 'hybrid' && '🔄 Hybrid'}
                    {client.detected_content_type && client.detected_content_type !== client.content_focus_type && (
                      <span className="ml-1 text-xs text-blue-400">(detected)</span>
                    )}
                  </>
                ) : (
                  <button 
                    onClick={() => setShowBrandTypeSettings(true)}
                    className="text-primary hover:text-primary/80 transition-colors"
                  >
                    + Set content focus
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border/50">
          <h3 className="font-semibold text-foreground mb-3">Audience</h3>
          <p className="text-sm text-muted-foreground mb-2">{client.target_audience}</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border/50">
          <h3 className="font-semibold text-foreground mb-3">Goals</h3>
          <div className="flex flex-wrap gap-2">
            {client.goals.map((goal, idx) => (
              <span key={idx} className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm">{goal}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Visual Intelligence Strategy Card */}
      {brandTypeInfo && brandTypeInfo.vi_strategy && (
        <div className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <FiZap className="text-purple-400" />
              Visual Intelligence
            </h3>
            {brandTypeInfo.stats?.total_count > 0 && (
              <span className="text-xs text-muted-foreground">
                {brandTypeInfo.stats.total_count} photos analyzed
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Strategy Type */}
            <div className="bg-background/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">
                  {brandTypeInfo.effective_type === 'product' && '📦'}
                  {brandTypeInfo.effective_type === 'space' && '🏠'}
                  {brandTypeInfo.effective_type === 'hybrid' && '🔄'}
                </span>
                <span className="font-medium text-foreground capitalize">{brandTypeInfo.effective_type || 'Unknown'} Focus</span>
              </div>
              <p className="text-sm text-muted-foreground">{brandTypeInfo.vi_strategy.description}</p>
            </div>
            
            {/* VI Features */}
            <div className="bg-background/50 rounded-lg p-4">
              <p className="text-xs text-purple-400 font-medium mb-2">AI OPTIMIZATIONS</p>
              <ul className="space-y-1">
                {brandTypeInfo.vi_strategy.vi_features?.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                    <FiCheckCircle className="text-emerald-400 flex-shrink-0" size={12} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Upload Stats */}
          {brandTypeInfo.stats?.total_count > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-500/10">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Product uploads:</span>
                  <span className="font-medium text-foreground">{brandTypeInfo.stats.product_count || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Space uploads:</span>
                  <span className="font-medium text-foreground">{brandTypeInfo.stats.space_count || 0}</span>
                </div>
                {brandTypeInfo.recommendation && (
                  <div className="flex-1 text-right text-xs text-purple-400">
                    {brandTypeInfo.recommendation}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Social & Timing Intelligence Section */}
      {(hasSocialHandles || hasSeasonalTrends) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {hasSocialHandles && (
            <div className="bg-card rounded-xl p-6 border border-border/50">
              <h3 className="font-semibold text-foreground mb-4 flex items-center">
                Social Profiles
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(client.social_handles).map(([platform, handle]) => {
                  if (!handle) return null;
                  const social = socialIcons[platform];
                  if (!social) return null;
                  const Icon = social.icon;
                  return (
                    <a
                      key={platform}
                      href={handle.startsWith('http') ? handle : `https://${platform}.com/${handle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary transition-colors"
                    >
                      <Icon className={social.color} size={18} />
                      <span className="text-sm text-muted-foreground truncate">{handle}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {hasSeasonalTrends && (
            <div className="bg-card rounded-xl p-6 border border-border/50">
              <h3 className="font-semibold text-foreground mb-4 flex items-center">
                <FiCalendar className="mr-2 text-primary" />
                Seasonal Intelligence
              </h3>
              <div className="space-y-3">
                {client.seasonal_trends.peak_months?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FiTrendingUp className="text-emerald-400 mt-0.5" size={16} />
                    <div>
                      <span className="text-xs text-muted-foreground uppercase">Peak Months</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {client.seasonal_trends.peak_months.map(month => (
                          <span key={month} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-xs">{month}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {client.seasonal_trends.low_months?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FiTrendingDown className="text-orange-400 mt-0.5" size={16} />
                    <div>
                      <span className="text-xs text-muted-foreground uppercase">Low Months</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {client.seasonal_trends.low_months.map(month => (
                          <span key={month} className="px-2 py-0.5 bg-orange-500/10 text-orange-400 rounded text-xs">{month}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {client.seasonal_trends.campaign_lead_time && (
                  <div className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Lead Time:</strong> {client.seasonal_trends.campaign_lead_time} weeks before peak
                  </div>
                )}
                {client.seasonal_trends.notes && (
                  <p className="text-sm text-muted-foreground italic">&ldquo;{client.seasonal_trends.notes}&rdquo;</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content Intelligence Section */}
      {insights && insights.summary.total_content > 0 && (
        <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/20 p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center">
            <FiBarChart2 className="mr-2 text-primary" />
            Content Intelligence
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg p-4 text-center border border-border/30">
              <p className="text-2xl font-heading font-bold text-foreground">{insights.summary.total_content}</p>
              <p className="text-sm text-muted-foreground">Total Ideas</p>
            </div>
            <div className="bg-card rounded-lg p-4 text-center border border-border/30">
              <p className="text-2xl font-heading font-bold text-emerald-400">{insights.summary.approved}</p>
              <p className="text-sm text-muted-foreground flex items-center justify-center">
                <FiCheckCircle className="mr-1" size={14} /> Approved
              </p>
            </div>
            <div className="bg-card rounded-lg p-4 text-center border border-border/30">
              <p className="text-2xl font-heading font-bold text-red-400">{insights.summary.rejected}</p>
              <p className="text-sm text-muted-foreground flex items-center justify-center">
                <FiXCircle className="mr-1" size={14} /> Rejected
              </p>
            </div>
            <div className="bg-card rounded-lg p-4 text-center border border-border/30">
              <p className="text-2xl font-heading font-bold text-primary">{insights.summary.approval_rate}%</p>
              <p className="text-sm text-muted-foreground">Approval Rate</p>
            </div>
          </div>

          {/* Platform Performance */}
          {Object.keys(insights.platform_performance || {}).length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">Platform Performance</h4>
              <div className="flex flex-wrap gap-3">
                {Object.entries(insights.platform_performance).map(([platform, stats]) => (
                  <div key={platform} className="bg-card rounded-lg px-4 py-2 flex items-center gap-2 border border-border/30">
                    <span className="capitalize font-medium text-foreground">{platform}</span>
                    <span className={`text-sm font-bold ${stats.approval_rate >= 70 ? 'text-emerald-400' : stats.approval_rate >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                      {stats.approval_rate}%
                    </span>
                    <span className="text-xs text-muted-foreground">({stats.approved}/{stats.total})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights - Now shows patterns AND opportunities */}
          {insights.ai_insights && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* What Works */}
              {insights.ai_insights.winning_patterns && (
                <div className="bg-card rounded-lg p-4 border border-border/30">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <FiCheckCircle className="mr-1 text-emerald-400" />
                    What Works for {client.name}
                  </h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    {insights.ai_insights.winning_patterns.emotional_tone && (
                      <p><span className="font-medium text-foreground">Tone:</span> {insights.ai_insights.winning_patterns.emotional_tone}</p>
                    )}
                    {insights.ai_insights.winning_patterns.hook_style && (
                      <p><span className="font-medium text-foreground">Hooks:</span> {insights.ai_insights.winning_patterns.hook_style}</p>
                    )}
                    {insights.ai_insights.winning_patterns.visual_approach && (
                      <p><span className="font-medium text-foreground">Visuals:</span> {insights.ai_insights.winning_patterns.visual_approach}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Fresh Opportunities */}
              {insights.ai_insights.exploration_opportunities && (
                <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-lg p-4 border border-accent/20">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <FiZap className="mr-1 text-accent" />
                    Fresh Opportunities
                  </h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    {insights.ai_insights.exploration_opportunities.untapped_emotions && (
                      <p><span className="font-medium text-purple-700">Try:</span> {insights.ai_insights.exploration_opportunities.untapped_emotions}</p>
                    )}
                    {insights.ai_insights.exploration_opportunities.angle_suggestions && (
                      <p><span className="font-medium text-purple-700">Angles:</span> {insights.ai_insights.exploration_opportunities.angle_suggestions}</p>
                    )}
                    {insights.ai_insights.exploration_opportunities.risk_worthy_ideas && (
                      <p className="italic text-purple-600">&ldquo;{insights.ai_insights.exploration_opportunities.risk_worthy_ideas}&rdquo;</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Balance recommendation */}
          {insights.ai_insights?.balance_recommendation && (
            <p className="text-xs text-muted-foreground mt-3 text-center italic">
              {insights.ai_insights.balance_recommendation}
            </p>
          )}
          
          {!insights.has_enough_data && (
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Approve or reject more content ideas to unlock AI-powered insights
            </p>
          )}
        </div>
      )}

      {/* Brand Assets Library */}
      <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
        <div className="p-6 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground flex items-center">
                <FiStar className="mr-2 text-yellow-400" />
                Brand Logo
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upload your logo to include it in generated content when appropriate
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAssetUpload}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAsset || brandAssets.filter(a => a.asset_type === 'logo').length >= 1}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="upload-brand-asset"
              >
                {uploadingAsset ? (
                  <FiLoader className="animate-spin" size={16} />
                ) : (
                  <FiUpload size={16} />
                )}
                <span>{brandAssets.filter(a => a.asset_type === 'logo').length >= 1 ? 'Logo Uploaded' : 'Upload Logo'}</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {brandAssets.filter(a => a.asset_type === 'logo').length === 0 ? (
            <div className="text-center py-8">
              <FiStar className="mx-auto text-yellow-400/50 mb-3" size={40} />
              <p className="text-muted-foreground mb-2">No logo uploaded yet</p>
              <p className="text-sm text-muted-foreground">
                Upload your brand logo and AI will intelligently include it in generated images when appropriate
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show only logos */}
              {(() => {
                const logos = brandAssets.filter(a => a.asset_type === 'logo');
                if (logos.length === 0) return null;
                
                return (
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {logos.map((asset) => (
                        <div key={asset.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border hover:border-primary/50 transition-colors">
                            <img 
                              src={asset.image_url} 
                              alt={asset.name} 
                              className="w-full h-full object-contain p-2"
                            />
                          </div>
                          <button
                            onClick={() => handleDeleteAsset(asset.id)}
                            disabled={deletingAssetId === asset.id}
                            className="absolute top-1 right-1 bg-red-500/90 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            title="Delete logo"
                          >
                            {deletingAssetId === asset.id ? (
                              <FiLoader className="animate-spin" size={12} />
                            ) : (
                              <FiTrash2 size={12} />
                            )}
                          </button>
                          <p className="text-xs text-muted-foreground mt-1 truncate text-center" title={asset.name}>
                            {asset.name}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      AI will decide when and how to include your logo in generated images
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* AI Learning Insights - Shows what AI has learned from approvals */}
      <AILearningInsights clientId={clientId} clientName={client.name} />

      <div>
        <h2 className="text-xl font-semibold mb-4">Campaigns</h2>
        {campaigns.length === 0 ? (
          <div className="bg-card rounded-lg shadow-sm p-12 text-center border border-border">
            <p className="text-muted-foreground mb-4">No campaigns yet. Create your first campaign to get started.</p>
            <button
              onClick={() => setShowCampaignModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {campaigns.map(campaign => (
              <div
                key={campaign.id}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                className="bg-card rounded-lg shadow-sm p-6 border border-border hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">{campaign.name}</h3>
                    <p className="text-sm text-muted-foreground">{campaign.objective.replace('_', ' ')}</p>
                  </div>
                  <FiChevronRight className="text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="text-sm text-foreground mb-4">
                  <strong>Creative Angle:</strong> {campaign.creative_angle}
                </div>
                <div className="flex flex-wrap gap-2">
                  {campaign.platforms.map((platform, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCampaignModal && (
        <CampaignCreationModal
          clientId={clientId}
          client={client}
          onClose={() => setShowCampaignModal(false)}
          onSuccess={() => {
            setShowCampaignModal(false);
            loadClientData();
          }}
        />
      )}

      {/* Brand Type Settings Modal */}
      {showBrandTypeSettings && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-lg border border-border/50 shadow-xl">
            <div className="p-6 border-b border-border/50">
              <h2 className="text-xl font-semibold text-foreground">Content Focus Settings</h2>
              <p className="text-sm text-muted-foreground mt-1">
                How do you typically create content? This helps AI optimize for your style.
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {[
                { id: 'product', icon: '📦', label: 'Product Photos', desc: 'Items, merchandise, physical products' },
                { id: 'space', icon: '🏠', label: 'Space Photos', desc: 'Interior, storefront, environment' },
                { id: 'hybrid', icon: '🔄', label: 'Both', desc: 'Mix of product and space photography' }
              ].map(option => (
                <button
                  key={option.id}
                  onClick={() => updateBrandType(option.id)}
                  disabled={updatingBrandType}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    (brandTypeInfo?.stated_type || client.content_focus_type) === option.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  } ${updatingBrandType ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`brand-type-option-${option.id}`}
                >
                  <span className="text-2xl">{option.icon}</span>
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.desc}</div>
                  </div>
                  {(brandTypeInfo?.stated_type || client.content_focus_type) === option.id && (
                    <FiCheckCircle className="text-primary" size={20} />
                  )}
                  {brandTypeInfo?.detected_type === option.id && brandTypeInfo?.detected_type !== (brandTypeInfo?.stated_type || client.content_focus_type) && (
                    <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded">Detected</span>
                  )}
                </button>
              ))}
              
              {brandTypeInfo?.stats?.total_count >= 3 && brandTypeInfo?.detected_type && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mt-4">
                  <p className="text-sm text-blue-300">
                    <strong>AI Detection:</strong> Based on {brandTypeInfo.stats.total_count} uploads, 
                    your content appears to be <strong className="capitalize">{brandTypeInfo.detected_type}</strong>-focused.
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-border/50 flex justify-end gap-3">
              <button
                onClick={() => setShowBrandTypeSettings(false)}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetail;
